import React, { useState, useCallback } from 'react';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Rectangle } from './types';

interface RectangleOverlayProps {
  rectangles: Rectangle[];
  selectedRectangle: { imageIndex: number; rectIndex: number } | null;
  imageIndex: number;
  onSelect: (imageIndex: number, rectIndex: number) => void;
  onDelete: () => void;
  onMove: (rectIndex: number, newX: number, newY: number) => void;
  // Transform parameters (deprecated - rectangles are now in container coordinates)
  position?: { x: number; y: number };
  zoom?: number;
}

export const RectangleOverlay: React.FC<RectangleOverlayProps> = ({
  rectangles,
  selectedRectangle,
  imageIndex,
  onSelect,
  onDelete,
  onMove,
  position = { x: 0, y: 0 },
  zoom = 1
}) => {
  const [dragState, setDragState] = useState<{
    isDragging: boolean;
    rectIndex: number | null;
    startX: number;
    startY: number;
    initialRectX: number;
    initialRectY: number;
  }>({
    isDragging: false,
    rectIndex: null,
    startX: 0,
    startY: 0,
    initialRectX: 0,
    initialRectY: 0
  });
  const handleMouseDown = useCallback((e: React.MouseEvent, rectIndex: number) => {
    e.stopPropagation();
    e.preventDefault();
    
    const rect = rectangles[rectIndex];
    setDragState({
      isDragging: true,
      rectIndex,
      startX: e.clientX,
      startY: e.clientY,
      initialRectX: rect.x,
      initialRectY: rect.y
    });
    
    onSelect(imageIndex, rectIndex);
  }, [imageIndex, onSelect, rectangles]);

  const handleMouseMove = useCallback((e: React.MouseEvent) => {
    if (!dragState.isDragging || dragState.rectIndex === null) return;
    
    e.stopPropagation();
    e.preventDefault();
    
    const deltaX = e.clientX - dragState.startX;
    const deltaY = e.clientY - dragState.startY;

    // Rectangle coordinates are in container space, use deltas directly
    const newX = Math.max(0, dragState.initialRectX + deltaX);
    const newY = Math.max(0, dragState.initialRectY + deltaY);
    
    onMove(dragState.rectIndex, newX, newY);
  }, [dragState, onMove]);

  const handleMouseUp = useCallback((e: React.MouseEvent) => {
    if (dragState.isDragging) {
      e.stopPropagation();
      e.preventDefault();
      setDragState({
        isDragging: false,
        rectIndex: null,
        startX: 0,
        startY: 0,
        initialRectX: 0,
        initialRectY: 0
      });
    }
  }, [dragState.isDragging]);

  const isSelected = (rectIndex: number) => {
    return selectedRectangle?.imageIndex === imageIndex && selectedRectangle?.rectIndex === rectIndex;
  };


  return (
    <div onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
      {rectangles.map((rect, rectIndex) => (
        <div key={rectIndex}>
          {/* Rectangle */}
          <div
            className={`absolute border-2 ${dragState.isDragging && dragState.rectIndex === rectIndex ? 'cursor-grabbing' : 'cursor-grab'}`}
            style={{
              backgroundColor: 'rgba(132, 204, 22, 0.2)', // lime-500 with 20% opacity
              borderColor: isSelected(rectIndex) ? '#84cc16' : 'rgba(132, 204, 22, 0.6)', // lime-500 or lime-500 with 60% opacity
              left: `${rect.x}px`,
              top: `${rect.y}px`,
              width: `${rect.width}px`,
              height: `${rect.height}px`,
              zIndex: 10
            }}
            onMouseDown={(e) => handleMouseDown(e, rectIndex)}
          >
            {/* Delete button - always visible */}
            <Button
              size="sm"
              variant="destructive"
              className="absolute h-6 w-6 p-0 z-50 opacity-80 hover:opacity-100"
              style={{
                right: '-12px',
                top: '-12px',
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                // Select the rectangle first, then delete it
                onSelect(imageIndex, rectIndex);
                setTimeout(() => onDelete(), 10); // Small delay to ensure selection happens first
              }}
              title="Delete Rectangle"
            >
              <Trash2 className="h-3 w-3" />
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};